/**
 * Single source for GEO-oriented copy: meta descriptions, JSON-LD summaries, and llms.txt.
 * Import from route views and from scripts/generate-sitemap.js (via @babel/register).
 */

const SECTION = {
    OVERVIEW: 'Overview',
    MEDIA_SERVICES: 'Media Services',
    DATA_SERVICES: 'Data Services',
    BLOG: 'Blog',
    LEGAL: 'Legal'
};

/**
 * One place to edit per-page GEO/meta descriptions. Reused by GEO_LLMS_PAGES and route views.
 * @type {Readonly<Record<string, string>>}
 */
export const GEO_PAGE_DESCRIPTIONS = Object.freeze({
    HOME: 'Hotmob is a leading data-centric media company based in Hong Kong, founded in 2007. We provide media services and data services across APAC and the GBA — from programmatic and premium placement to market intelligence, insights, and AI-driven creative.',
    MEDIA_SERVICE:
        'Hotmob media services in Hong Kong elevate advertising strategies, integrating programmatic buys and premium placements for targeted, multichannel campaigns.',
    DATA_DRIVEN_PROGRAMMATIC:
        'Engage audiences with precision. Hotmob powers data-driven programmatic advertising in Hong Kong through real-time bidding and strategic audience segmentation.',
    PREMIUM_PLACEMENT:
        'Hotmob curates premium ad placements across high-profile Hong Kong publishers, capturing audience attention with guaranteed brand safety and high viewability.',
    CAMPAIGN_MANAGEMENT_SERVICE:
        'Hotmob provides campaign management services in Hong Kong, covering strategic media planning, cross-channel execution, and performance tracking.',
    CHINA_MEDIA_SOLUTIONS:
        'Hotmob delivers China media solutions for APAC brands, including localized social platform activation, KOL network coordination, and cross-border channel strategy.',
    AI_CONTENT_CREATION:
        'Reimagine content workflows. Hotmob leverages AI creation tools in Hong Kong to automate ad copy, visual design, and machine learning-driven optimization.',
    DATA_SERVICE:
        'Hotmob is a Hong Kong data provider that unifies internal and external information for market intelligence and consumer insight, helping teams make strategic business decisions.',
    EXTERNAL_DATA:
        'Hotmob data enrichment synchronizes proprietary customer records with licensed external datasets to identify behavioral patterns. This provides a multidimensional view of market trends while ensuring full compliance with regional data privacy and security standards.',
    INSIGHT_REPORT:
        'Hotmob generates localized reports based on the systematic analysis of mobile usage and cross-platform consumption patterns. The report provided objective assessments of audience engagement and market shifts, delivering granular metrics for verified business forecasting.',
    ETL_PROJECT:
        'Hotmob runs end-to-end data integration projects that automatically move scattered data into structured warehouses, using consistent mapping and secure connections to keep data reliable and operations efficient.',
    BLOG: 'Articles on Hong Kong tech news, digital marketing, proprietary-style data findings, and campaign showcases—published by Hotmob.',
    PRIVACY_POLICY:
        "Trust and transparency drive our operations. Hotmob's privacy policy outlines secure data processing and compliance protocols for our digital marketing services.",
    PAGE_NOT_FOUND: "The page you requested could not be found. Return to Hotmob's homepage or explore our advertising and media services."
});

/** Short line used when dynamic copy is missing and for brand-adjacent fallbacks */
export const GEO_SITE_TAGLINE = 'A Data-Centric Media Company';

/** Default Open Graph `og:site_name` (blog and shared SEO components) */
export const GEO_OG_SITE_NAME = `Hotmob | ${GEO_SITE_TAGLINE}`;

/** Opening quote block in llms.txt */
export const GEO_LLMS_INTRO =
    'Pioneering Hong Kong digital trends since 2007, Hotmob empowers brands with first-party data intelligence, AI ad solutions, and omnichannel media services.';

export const GEO_CONTACT = {
    phone: ['+852 3728 2400', '+852 3728 2405'],
    fax: '+852 2851 6601',
    address: '20/F., Harbourside HQ, 8 Lam Chak Street, Kowloon Bay, Hong Kong'
};

/**
 * Ordered entries for llms.txt (section order follows first occurrence in this array).
 * @type {{ path: string, title: string, description: string, section: string }[]}
 */
export const GEO_LLMS_PAGES = [
    { path: '', title: 'Hotmob', description: GEO_PAGE_DESCRIPTIONS.HOME, section: SECTION.OVERVIEW },
    {
        path: '/media-service',
        title: 'Media Service',
        description: GEO_PAGE_DESCRIPTIONS.MEDIA_SERVICE,
        section: SECTION.MEDIA_SERVICES
    },
    {
        path: '/data-driven-programmatic',
        title: 'Data-driven Programmatic',
        description: GEO_PAGE_DESCRIPTIONS.DATA_DRIVEN_PROGRAMMATIC,
        section: SECTION.MEDIA_SERVICES
    },
    {
        path: '/premium-placement',
        title: 'Premium Placement',
        description: GEO_PAGE_DESCRIPTIONS.PREMIUM_PLACEMENT,
        section: SECTION.MEDIA_SERVICES
    },
    {
        path: '/campaign-management-service',
        title: 'Campaign Management Service',
        description: GEO_PAGE_DESCRIPTIONS.CAMPAIGN_MANAGEMENT_SERVICE,
        section: SECTION.MEDIA_SERVICES
    },
    {
        path: '/china-media-solutions',
        title: 'China Media Solutions',
        description: GEO_PAGE_DESCRIPTIONS.CHINA_MEDIA_SOLUTIONS,
        section: SECTION.MEDIA_SERVICES
    },
    {
        path: '/ai-creation',
        title: 'AI Creation',
        description: GEO_PAGE_DESCRIPTIONS.AI_CONTENT_CREATION,
        section: SECTION.MEDIA_SERVICES
    },
    {
        path: '/data-service',
        title: 'Data Service',
        description: GEO_PAGE_DESCRIPTIONS.DATA_SERVICE,
        section: SECTION.DATA_SERVICES
    },
    {
        path: '/external-data',
        title: 'External Data',
        description: GEO_PAGE_DESCRIPTIONS.EXTERNAL_DATA,
        section: SECTION.DATA_SERVICES
    },
    {
        path: '/insight-report',
        title: 'Insight Report',
        description: GEO_PAGE_DESCRIPTIONS.INSIGHT_REPORT,
        section: SECTION.DATA_SERVICES
    },
    {
        path: '/etl-project',
        title: 'ETL Project',
        description: GEO_PAGE_DESCRIPTIONS.ETL_PROJECT,
        section: SECTION.DATA_SERVICES
    },
    {
        path: '/blog/all/1',
        title: 'Blog',
        description: GEO_PAGE_DESCRIPTIONS.BLOG,
        section: SECTION.BLOG
    },
    {
        path: '/privacy-policy',
        title: 'Privacy Policy',
        description: GEO_PAGE_DESCRIPTIONS.PRIVACY_POLICY,
        section: SECTION.LEGAL
    }
];
